import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient, ApolloError } from '@apollo/client';
import { Button, Divider, Space, Modal, message } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouterSpecsFragment } from '../../../api/fragments/RouterSpecs';
import {
    DeleteEndpointDocument,
    type DeleteEndpointMutation,
    type DeleteEndpointMutationVariables,
} from '../../../api/mutations/deleteEndpoint';
import { useEndpointUpdateForm } from './EndpointUpdateForm';

export type EndpointActionsProps = {
    router: RouterSpecsFragment;
    endpoint: RouterSpecsFragment['endpoints'][number];
};

const EndpointActions = ({ router, endpoint }: EndpointActionsProps) => {
    const routerId = router.id;
    const endpointId = endpoint.id;

    const apolloClient = useApolloClient();
    const { t } = useTranslation(['routerDetails']);

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('routerDetails:endpoints.deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('routerDetails:endpoints.deleteModal.content'),
            okText: t('routerDetails:endpoints.deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('routerDetails:endpoints.deleteModal.cancelButton'),
            onOk: async () => {
                try {
                    message.loading({
                        content: t('routerDetails:endpoints.deleteModal.messages.submitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    await apolloClient
                        .mutate<DeleteEndpointMutation, DeleteEndpointMutationVariables>({
                            mutation: DeleteEndpointDocument,
                            variables: { routerId, endpointId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    message.success({
                        content: t('routerDetails:endpoints.deleteModal.messages.success'),
                        key: 'primary',
                    });
                } catch (error) {
                    if (error instanceof ApolloError) {
                        message.error(error.graphQLErrors[0].message);
                    }
                }
            },
        });
    }, [apolloClient, routerId, endpointId, t]);

    const updateForm = useEndpointUpdateForm();

    return (
        <>
            <Space size="small" split={<Divider type="vertical" />}>
                <Button onClick={updateForm.open} size="small" style={{ padding: 0 }} type="link">
                    {t('routerDetails:endpointTable.actions.update')}
                </Button>
                <Button onClick={deleteAction} size="small" style={{ padding: 0 }} type="link">
                    {t('routerDetails:endpointTable.actions.delete')}
                </Button>
            </Space>
            {updateForm.render({ router, endpoint })}
        </>
    );
};

export default EndpointActions;
